import config from "../config";

/**
 * Generic safe fetch wrapper - mirrors RateService.jsx's convention (incl.
 * the headers-merge fix - see that file's comment for why it matters).
 */
const safeFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: { ...config.getHeaders(), ...options.headers },
  });

  if (!res.ok) {
    const errorText = await res.text();
    console.error("API Error:", errorText);
    let message = errorText;
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.message || parsed.error || errorText;
    } catch {
      // not JSON - fall back to raw text
    }
    throw new Error(message || "API request failed");
  }

  if (res.status === 204) {
    return null;
  }

  const contentType = res.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    return null;
  }
  return res.json();
};

export const fetchResorts = async () => {
  return safeFetch(`${config.BASE_URL}/api/resorts`);
};

// Single-resort fetch - the only one that includes photos (see
// ResortMapper.toResortDetailDTO on the backend). fetchResorts() above is
// used by many unrelated screens (ManageResorts, CreateBookingForm, Rate
// Management...) and deliberately excludes photos to avoid adding N+1
// queries + payload weight to all of them - this page fetches a resort's
// full content only when an admin actually selects it to edit here.
export const fetchResortById = async (resortId) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/${resortId}`);
};

// SUPER_ADMIN only (see ResortController.updateResortDetails) - the
// structured guest-facing fields (amenities, check-in/out, meal plan,
// cancellation policy, catch-all notes) the WhatsApp chatbot grounds FAQ
// answers in, plus the guest-website content (tagline, vibe tags, listing
// badge, room category descriptions/base+max occupancy) saved from the same form.
export const updateResortDetails = async (resortId, details) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/details`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(details),
  });
};

// SUPER_ADMIN only (see ResortController.updateResortSeo) - just the
// microsite meta title/description override, SeoSettings.jsx's own narrow
// form. Deliberately a separate endpoint from updateResortDetails above,
// not folded into it - see ResortSeoRequest's javadoc on the backend for
// why a page that only edits these two fields can't safely share that
// save-everything endpoint.
export const updateResortSeo = async (resortId, seo) => {
  return safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/seo`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(seo),
  });
};

/**
 * Multipart upload - deliberately does NOT use safeFetch/config.getHeaders()
 * (that always forces Content-Type: application/json, which would break the
 * multipart boundary the browser needs to set itself for FormData). Only
 * the Authorization header is needed here.
 */
const uploadPhoto = async (url, file) => {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: token ? `Bearer ${token}` : "" },
    body: formData,
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Upload failed");
  }
  return res.json();
};

// Resort-level gallery photos (listing card + microsite hero) - upload
// happens immediately on file select, not deferred to the details Save.
export const uploadResortPhoto = (resortId, file) =>
  uploadPhoto(`${config.BASE_URL}/api/resorts/${resortId}/photos`, file);

export const deleteResortPhoto = (resortId, photoId) =>
  safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/photos/${photoId}`, { method: "DELETE" });

// Room-category gallery photos (microsite room list + booking flow cards).
export const uploadRoomCategoryPhoto = (resortId, categoryId, file) =>
  uploadPhoto(`${config.BASE_URL}/api/resorts/${resortId}/room-categories/${categoryId}/photos`, file);

export const deleteRoomCategoryPhoto = (resortId, categoryId, photoId) =>
  safeFetch(
    `${config.BASE_URL}/api/resorts/${resortId}/room-categories/${categoryId}/photos/${photoId}`,
    { method: "DELETE" }
  );

// Relative path (e.g. "public/resorts/12/uuid.jpg") -> displayable URL.
// Public, unauthenticated media - no access token appended, unlike
// getFileViewUrl's /api/files/** guest-document URLs.
export const publicMediaUrl = (relativePath) =>
  relativePath ? `${config.BASE_URL}/api/public-media/${relativePath}` : null;

// Resort FAQ entries - free-text question/answer pairs, e.g. "How far is
// the beach?". Same immediately-saved-on-action convention as the photo
// functions above (not part of updateResortDetails' batched save).
export const addResortFaq = (resortId, faq) =>
  safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/faqs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(faq),
  });

export const updateResortFaq = (resortId, faqId, faq) =>
  safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/faqs/${faqId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(faq),
  });

export const deleteResortFaq = (resortId, faqId) =>
  safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/faqs/${faqId}`, { method: "DELETE" });

// Returns the resort's full, newly-ordered FAQ list - a move swaps two
// entries' order, not just the one being moved (see
// ResortService.moveFaq's javadoc on the backend).
export const moveResortFaq = (resortId, faqId, direction) =>
  safeFetch(`${config.BASE_URL}/api/resorts/${resortId}/faqs/${faqId}/move?direction=${direction}`, {
    method: "PUT",
  });
